import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Tag,
  ShoppingBag,
  Check,
  Plus,
  ExternalLink,
  X,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { ProductCard } from "@/components/ProductCard";
import { Button } from "@/components/ui/button";
import { externalLinkProps } from "@/lib/externalLink";
import { withUtm } from "@/lib/utm";
import { toast } from "sonner";

/**
 * Sale landing page. Fetches /api/sales/{id} — backend parses the
 * banner description into structured rules (once, cached forever after)
 * and returns the qualifying products. If no rules could be extracted
 * the API still returns every non-out-of-stock product from that
 * dispensary so the page never feels empty.
 *
 * On top of the product grid we render a lightweight "match basket".
 * Bud Link cannot actually push items to a Trulieve/Dutchie cart (that
 * needs partner integration), but we CAN show the user which items
 * qualify for the offer, how many more they still need, and hand them
 * off to Trulieve with UTM-tagged deep-links per item so conversions
 * are trackable.
 *
 * When rules include a BOGO / min_qty target the bar shows explicit
 * progress ("2 of 3 qualified — save $18"); for percent-off or
 * un-parseable offers it falls back to a plain counter with total
 * savings.
 */
export default function SaleDetailPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [basket, setBasket] = useState({}); // { [product.id]: product }

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .get(`/sales/${id}`)
      .then((r) => live && setData(r.data))
      .catch((e) => {
        if (live) toast.error(formatApiError(e));
      })
      .finally(() => live && setLoading(false));
    return () => {
      live = false;
    };
  }, [id]);

  // Reset basket when we navigate to a different sale.
  useEffect(() => {
    setBasket({});
  }, [id]);

  const utm = useMemo(
    () => ({
      medium: "sale",
      campaign: data?.sale?.id || id,
      content: "sale-card",
    }),
    [data?.sale?.id, id],
  );

  const saleId = data?.sale?.id || id;

  const rules = data?.rules || {};
  const target = deriveTarget(rules);
  const basketItems = Object.values(basket);
  const savings = computeSavings(basketItems, rules);

  const inBasket = (pid) => Boolean(basket[pid]);
  const toggle = (product) => {
    setBasket((prev) => {
      const next = { ...prev };
      if (next[product.id]) delete next[product.id];
      else next[product.id] = product;
      return next;
    });
  };
  const clearBasket = () => setBasket({});

  if (loading) {
    return (
      <main className="container-narrow py-16 flex items-center justify-center text-foreground/60">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading sale…
      </main>
    );
  }
  if (!data?.sale) {
    return (
      <main className="container-narrow py-16 text-center">
        <p className="text-foreground/70 mb-4">Sale not found or expired.</p>
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-emerald-300 hover:text-emerald-200"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> Back home
        </Link>
      </main>
    );
  }

  const { sale, products = [] } = data;
  const ruleChips = [
    rules.category && { label: rules.category, k: "cat" },
    rules.subcategory && { label: rules.subcategory.toUpperCase(), k: "sub" },
    rules.size_bucket && { label: rules.size_bucket, k: "size" },
    rules.strain_type && { label: rules.strain_type, k: "type" },
    rules.brand && { label: rules.brand, k: "brand" },
    rules.discount_percent && {
      label: `${rules.discount_percent}% off`,
      k: "pct",
    },
    rules.discount_kind === "bogo" && { label: "BOGO", k: "bogo" },
    // Excluded brands render as red-tinted chips so shoppers see at a
    // glance which brands the sale DOESN'T apply to (Trulieve headlines
    // like "35% off All Smalls (Excludes R.O. & Connected)").
    ...(Array.isArray(rules.excluded_brands) ? rules.excluded_brands : []).map(
      (b, i) => ({ label: `No ${b}`, k: `ex-${i}`, excluded: true }),
    ),
  ].filter(Boolean);

  const qualified = target ? basketItems.length >= target : basketItems.length > 0;

  return (
    <main
      className="container-narrow py-8 pb-40"
      data-testid="sale-detail-page"
    >
      <Link
        to={`/dispensary/${sale.dispensary_slug}`}
        className="inline-flex items-center gap-1 text-xs uppercase tracking-wider text-foreground/60 hover:text-foreground mb-5"
      >
        <ArrowLeft className="w-3 h-3" />
        Back to dispensary
      </Link>

      <section className="rounded-3xl border border-emerald-500/40 bg-gradient-to-br from-emerald-950/50 via-card to-card p-6 sm:p-8">
        <div className="flex items-center gap-2 mb-3 text-emerald-300 text-[10px] uppercase tracking-[0.18em]">
          <Tag className="w-3.5 h-3.5" />
          Trulieve sale
          {sale.ends_at && (
            <span className="text-foreground/50">
              · ends{" "}
              {new Date(sale.ends_at).toLocaleDateString(undefined, {
                month: "short",
                day: "numeric",
              })}
            </span>
          )}
        </div>
        <h1
          className="font-serif text-2xl sm:text-4xl leading-tight text-foreground"
          data-testid="sale-headline"
        >
          {sale.headline || sale.title}
        </h1>
        {sale.description && sale.description !== sale.headline && (
          <p className="text-sm text-foreground/75 mt-3 leading-relaxed">
            {sale.description}
          </p>
        )}
        {ruleChips.length > 0 && (
          <div
            className="flex flex-wrap gap-1.5 mt-4"
            data-testid="sale-rule-chips"
          >
            {ruleChips.map((c) => (
              <span
                key={c.k}
                className={`inline-flex items-center rounded-full border text-[11px] px-2.5 h-6 capitalize ${
                  c.excluded
                    ? "border-red-500/40 bg-red-500/10 text-red-200"
                    : "border-emerald-500/40 bg-emerald-500/10 text-emerald-100"
                }`}
              >
                {c.label}
              </span>
            ))}
          </div>
        )}
        <p className="mt-4 text-[11px] text-foreground/50 leading-relaxed">
          Bud Link matches items to this offer — checkout happens on
          Trulieve. Tap products to build your match, then open each on
          Trulieve to add to your cart.
        </p>
      </section>

      <div className="mt-8">
        <div className="flex items-center gap-2 mb-3 text-[10px] uppercase tracking-[0.18em] text-foreground/60">
          <ShoppingBag className="w-3.5 h-3.5" />
          {products.length > 0 ? (
            <>
              Products that qualify ·{" "}
              <span className="text-emerald-300">{products.length}</span>
            </>
          ) : (
            <>No qualifying products in stock right now</>
          )}
        </div>
        {products.length > 0 && (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <SaleProductPick
                key={p.id}
                product={p}
                utm={utm}
                inBasket={inBasket(p.id)}
                onToggle={() => toggle(p)}
              />
            ))}
          </div>
        )}
      </div>

      {basketItems.length > 0 && (
        <BasketBar
          items={basketItems}
          target={target}
          savings={savings}
          qualified={qualified}
          rules={rules}
          onClear={clearBasket}
          onRemove={(pid) =>
            setBasket((prev) => {
              const next = { ...prev };
              delete next[pid];
              return next;
            })
          }
          utm={utm}
          saleId={sale.id || id}
        />
      )}
    </main>
  );
}

// ---------------------------------------------------------------------------
// Basket helpers
// ---------------------------------------------------------------------------

// Derive an integer qualifying-count target from parsed rules. Returns
// null when the offer isn't quantity-gated (e.g. "20% off flower") — the
// UI falls back to a plain counter.
function deriveTarget(rules) {
  if (!rules) return null;
  if (rules.discount_kind === "bogo") {
    // "BOGO" = buy one get one → 2. Claude/regex fills min_qty=2 for
    // classic BOGO and higher for "Buy 3 Get 1" style offers.
    return Math.max(2, Number(rules.min_qty) || 2);
  }
  const n = Number(rules.min_qty);
  if (Number.isFinite(n) && n >= 2) return n;
  return null;
}

function computeSavings(items, rules) {
  if (!items.length) return 0;
  const prices = items.map((p) => Number(p.price || 0)).sort((a, b) => a - b);

  if (rules?.discount_kind === "bogo") {
    // Classic "Buy N-1 Get 1 Free": every Nth item is free, discount
    // applies to the cheapest ones so the user actually sees savings.
    const N = Math.max(2, Number(rules.min_qty) || 2);
    const freebies = Math.floor(prices.length / N);
    return prices.slice(0, freebies).reduce((s, p) => s + p, 0);
  }

  if (Number.isFinite(Number(rules?.discount_percent))) {
    const pct = Number(rules.discount_percent) / 100;
    return prices.reduce((s, p) => s + p * pct, 0);
  }

  // Fallback: sum the per-item card-level discount (deal_active items)
  // so users still see something meaningful for percent-less rules.
  return items.reduce((s, p) => {
    if (
      p.deal_active &&
      Number(p.original_price) > Number(p.price)
    ) {
      return s + (Number(p.original_price) - Number(p.price));
    }
    return s;
  }, 0);
}

// ---------------------------------------------------------------------------
// Card wrapper — a tap-to-add pill above each ProductCard. Keeps the
// original ProductCard un-touched (compare toggle, favourite, etc. all
// keep working). One test id per pick so the testing agent can drive it.
// ---------------------------------------------------------------------------
function SaleProductPick({ product, utm, inBasket, onToggle }) {
  return (
    <div
      className="flex flex-col gap-2"
      data-testid={`sale-pick-${product.id}`}
    >
      <button
        type="button"
        onClick={onToggle}
        data-testid={`sale-pick-toggle-${product.id}`}
        aria-pressed={inBasket}
        className={`self-start inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.16em] font-semibold px-3 h-8 rounded-full border transition-colors ${
          inBasket
            ? "bg-emerald-500/20 text-emerald-200 border-emerald-500/50"
            : "bg-card text-foreground/80 border-border hover:border-emerald-500/40 hover:text-emerald-200"
        }`}
      >
        {inBasket ? (
          <>
            <Check className="w-3.5 h-3.5" strokeWidth={2.4} /> In match
          </>
        ) : (
          <>
            <Plus className="w-3.5 h-3.5" strokeWidth={2.4} /> Add to match
          </>
        )}
      </button>
      <ProductCard product={product} utm={utm} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sticky bottom bar showing progress + "Open on Trulieve" hand-off.
// Deliberately compact on mobile — collapses to a horizontal scroll of
// thumbnails when the basket has >3 items so it doesn't eat screen height.
// ---------------------------------------------------------------------------
function BasketBar({
  items,
  target,
  savings,
  qualified,
  rules,
  onClear,
  onRemove,
  utm,
  saleId,
}) {
  const openAll = () => {
    // Best-effort: iOS blocks popup loops so only the first item lands,
    // but on desktop Chrome/Firefox users get every item in a new tab.
    items.forEach((p, idx) => {
      const url = withUtm(p.order_url, {
        ...utm,
        content: "basket-open-all",
      });
      if (!url) return;
      // Slight stagger helps some browsers not block the loop.
      window.setTimeout(() => window.open(url, "_blank", "noopener"), idx * 30);
    });
  };

  const heading = target ? (
    <>
      <span
        className={qualified ? "text-emerald-300" : "text-foreground"}
        data-testid="basket-progress-count"
      >
        {items.length} of {target}
      </span>{" "}
      {qualified ? "qualified" : "matched"}
    </>
  ) : (
    <>
      <span
        className="text-foreground"
        data-testid="basket-progress-count"
      >
        {items.length}
      </span>{" "}
      {items.length === 1 ? "item" : "items"} in your match
    </>
  );

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-40 border-t border-emerald-500/30 bg-card/95 backdrop-blur-xl"
      data-testid="sale-basket-bar"
    >
      <div className="container-narrow py-3 sm:py-4">
        <div className="flex items-start justify-between gap-3 mb-2 sm:mb-3">
          <div className="min-w-0">
            <div
              className="text-sm sm:text-base font-medium"
              data-testid="basket-progress-heading"
            >
              {heading}
              {savings > 0 && (
                <>
                  {" "}
                  · you save{" "}
                  <span
                    className="text-emerald-300 font-semibold"
                    data-testid="basket-savings"
                  >
                    ${savings.toFixed(2)}
                  </span>
                </>
              )}
            </div>
            {target && !qualified && (
              <div className="text-[11px] text-foreground/60 mt-0.5">
                Add {target - items.length} more to unlock this deal.
              </div>
            )}
            {qualified && (
              <div className="text-[11px] text-emerald-300/90 mt-0.5">
                You&apos;re qualified — open each item on Trulieve to add
                them at checkout.
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClear}
            data-testid="basket-clear"
            className="text-[11px] uppercase tracking-[0.16em] text-foreground/60 hover:text-foreground/90 shrink-0"
          >
            Clear
          </button>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1 min-w-0 flex gap-2 overflow-x-auto no-scrollbar">
            {items.map((p) => (
              <a
                key={p.id}
                {...externalLinkProps(
                  withUtm(p.order_url, {
                    ...utm,
                    content: "basket-thumb",
                  }),
                )}
                data-testid={`basket-thumb-${p.id}`}
                className="group relative shrink-0 w-14 h-14 rounded-lg overflow-hidden border border-border bg-white"
                title={`${p.strain_name} — Open on Trulieve`}
              >
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.strain_name}
                    className="w-full h-full object-contain"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-[10px] text-muted-foreground px-1 text-center">
                    {(p.strain_name || "").slice(0, 12)}
                  </div>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onRemove(p.id);
                  }}
                  data-testid={`basket-remove-${p.id}`}
                  aria-label={`Remove ${p.strain_name}`}
                  className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-card/90 border border-border flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <X className="w-2.5 h-2.5" strokeWidth={2.4} />
                </button>
              </a>
            ))}
          </div>

          <Button
            type="button"
            onClick={openAll}
            disabled={!items.length}
            data-testid="basket-open-all"
            className={`shrink-0 h-11 rounded-full px-5 ${
              qualified
                ? "bg-emerald-500 hover:bg-emerald-500/90 text-emerald-950"
                : "bg-primary hover:bg-primary/90 text-primary-foreground"
            }`}
          >
            <ExternalLink className="w-4 h-4 mr-2" strokeWidth={2} />
            Open {items.length > 1 ? `all ${items.length}` : ""} on
            Trulieve
          </Button>
        </div>

        <p className="text-[10px] text-foreground/40 mt-2 leading-relaxed hidden sm:block">
          Campaign: {saleId?.slice(0, 8) || "sale"} · UTM-tagged for
          attribution.
        </p>
      </div>
    </div>
  );
}
